import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Construction, type LucideIcon } from 'lucide-react';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { Button } from '@/components/ui/button';

export interface MockStat {
  label: string;
  value: string;
  hint?: string;
}

export interface MockRow {
  id: string;
  primary: string;
  secondary?: string;
  meta?: string;
  badge?: string;
  badgeTone?: 'neutral' | 'success' | 'warn' | 'danger' | 'info';
}

interface CommunicationsMockPageProps {
  title: string;
  description: string;
  crumbs: { label: string; href?: string }[];
  icon: LucideIcon;
  stats?: MockStat[];
  columns: string[];
  rows: MockRow[];
  actions?: ReactNode;
  emptyHint?: string;
}

const BADGE: Record<NonNullable<MockRow['badgeTone']>, string> = {
  neutral: 'bg-gray-100 text-gray-700',
  success: 'bg-emerald-50 text-emerald-700',
  warn: 'bg-amber-50 text-amber-800',
  danger: 'bg-red-50 text-red-700',
  info: 'bg-blue-50 text-blue-700',
};

/** Shared mock shell for Communications screens (no API yet). */
export function CommunicationsMockPage({
  title,
  description,
  crumbs,
  icon: Icon,
  stats = [],
  columns,
  rows,
  actions,
  emptyHint = 'Mock data only — API wiring comes later.',
}: CommunicationsMockPageProps) {
  useBreadcrumbs(crumbs);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="mb-1 flex items-center gap-3">
            <Icon className="h-7 w-7 text-blue-700" />
            <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
            <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-800">
              <Construction className="h-3 w-3" />
              Mock
            </span>
          </div>
          <p className="ml-10 max-w-2xl text-sm text-gray-500">{description}</p>
        </div>
        {actions}
      </div>

      {stats.length > 0 && (
        <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {stats.map((s) => (
            <div key={s.label} className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
              <p className="text-sm text-gray-500">{s.label}</p>
              <p className="mt-1 text-2xl font-semibold text-gray-900">{s.value}</p>
              {s.hint && <p className="mt-1 text-xs text-gray-400">{s.hint}</p>}
            </div>
          ))}
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {columns.map((c) => (
                  <th
                    key={c}
                    className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                  >
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {rows.length === 0 && (
                <tr>
                  <td colSpan={columns.length} className="px-6 py-16 text-center text-sm text-gray-500">
                    {emptyHint}
                  </td>
                </tr>
              )}
              {rows.map((row) => (
                <tr key={row.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">{row.primary}</div>
                    {row.secondary && (
                      <div className="mt-0.5 text-xs text-gray-500">{row.secondary}</div>
                    )}
                  </td>
                  {columns.length > 1 && (
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {row.badge ? (
                        <span
                          className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                            BADGE[row.badgeTone ?? 'neutral']
                          }`}
                        >
                          {row.badge}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                  )}
                  {columns.length > 2 && (
                    <td className="px-6 py-4 text-sm text-gray-500">{row.meta ?? '—'}</td>
                  )}
                  {columns.length > 3 && <td className="px-6 py-4 text-right text-xs text-gray-400">Preview</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="border-t border-gray-100 px-6 py-3 text-xs text-gray-400">{emptyHint}</div>
      </div>
    </div>
  );
}

export function MockPrimaryButton({ children }: { children: ReactNode }) {
  return (
    <Button type="button" className="gap-2" onClick={() => undefined}>
      {children}
    </Button>
  );
}

export function MockNavChips({
  items,
}: {
  items: { label: string; href: string; active?: boolean }[];
}) {
  return (
    <div className="mb-4 flex flex-wrap gap-2">
      {items.map((item) => (
        <Link
          key={item.href}
          to={item.href}
          className={`rounded-md border px-3 py-1.5 text-sm transition ${
            item.active
              ? 'border-blue-200 bg-blue-50 text-blue-800'
              : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
          }`}
        >
          {item.label}
        </Link>
      ))}
    </div>
  );
}
